/**
 * Partner documents: which documents each partner handling the body needs, and
 * whether each one has been sent or received on a case.
 * Plan: docs/roadmap/CREMATORIUM_DOCS_FOLLOWUP_PLAN.md.
 *
 * KEEP IN SYNC: this file is copied byte-for-byte to
 * packages/admin/src/components/dispatch/partnerDocRequirements.ts, and
 * partner-doc-requirements.spec.ts fails if the two differ. It is pure (no
 * imports) so both packages can use it as-is.
 *
 * Rules attach to the ROLE a partner plays on the case (the funerals column
 * that points at it), never to who the partner is: MacKinnon as the drop-off
 * location needs the MCCD, MacKinnon as transport needs nothing.
 */

export type DocDirection = 'outbound' | 'inbound'; // outbound = we send it; inbound = they send it to us
export type PartnerRole = 'crematorium' | 'dropoff' | 'transport';

export interface PartnerFacts {
  id: string;
  name: string;
  email?: string | null;
  region?: string | null;
  is_crematorium?: boolean | null;
  agreement_type?: string | null;
}

export interface PartnerDocRequirement {
  /** funeral_documents.document_type */
  documentType: string;
  label: { en: string; fr: string };
  defaultDirection: DocDirection;
  /**
   * Inbound only: the requirement is met as soon as the document is on file for
   * the case, whoever uploaded it (the partner through the portal, or Cleo with
   * an electronic copy). We never send ours to them instead (T-051).
   */
  satisfiedByOnFile?: boolean;
}

export interface PartnerDocRule {
  id: string;
  /** By region / agreement_type / is_crematorium — never by name. */
  match: (p: PartnerFacts) => boolean;
  role: PartnerRole;
  /** Per-rule kill switch. */
  enabled: boolean;
  requirements: PartnerDocRequirement[];
}

export const PARTNER_DOC_RULES: PartnerDocRule[] = [
  {
    id: 'quebec_crematorium',
    match: (p) => p.region === 'quebec' && !!p.is_crematorium,
    role: 'crematorium',
    enabled: true,
    requirements: [
      {
        documentType: 'sp3',
        label: { en: 'SP3', fr: 'SP3' },
        defaultDirection: 'outbound',
      },
      {
        documentType: 'cremation_agreement',
        label: { en: 'cremation agreement', fr: 'entente de crémation' },
        defaultDirection: 'outbound',
      },
    ],
  },
  // The MCCD comes from WHICHEVER partner is the case's Ontario drop-off
  // location: Brampton VC, Ajax VC, or MacKinnon when it's set as the drop-off.
  // A paper MCCD is uploaded by the drop-off location through the portal; an
  // electronic one is uploaded by Cleo and the drop-off location doesn't need
  // it. Either way, one on file for the case is all that matters (T-051).
  {
    id: 'ontario_dropoff',
    match: (p) => p.region === 'ontario',
    role: 'dropoff',
    enabled: true,
    requirements: [
      {
        documentType: 'medical_certificate',
        label: {
          en: 'Medical Certificate of Death',
          fr: 'Medical Certificate of Death',
        },
        defaultDirection: 'inbound',
        satisfiedByOnFile: true,
      },
    ],
  },
  // Every Ontario cremation has a coroner's cremation certificate (Q12).
  {
    id: 'ontario_crematorium',
    match: (p) => p.region === 'ontario' && !!p.is_crematorium,
    role: 'crematorium',
    enabled: true,
    requirements: [
      {
        documentType: 'coroner_certificate',
        label: {
          en: "Coroner's Cremation Certificate",
          fr: "Coroner's Cremation Certificate",
        },
        defaultDirection: 'outbound',
      },
    ],
  },
  // Southwest takes the SIGNED burial permit (the scanned original), NOT the
  // City-issued copy (burial_permit_issued) (Q12).
  {
    id: 'southwest_crematorium',
    match: (p) => p.agreement_type === 'southwest',
    role: 'crematorium',
    enabled: true,
    requirements: [
      {
        documentType: 'burial_permit_signed',
        label: { en: 'Burial Permit (signed)', fr: 'Burial Permit (signed)' },
        defaultDirection: 'outbound',
      },
    ],
  },
];

/** The funerals column behind each role. */
export const ROLE_COLUMN: Record<
  PartnerRole,
  'crematorium_partner_id' | 'dropoff_partner_id' | 'transport_partner_id'
> = {
  crematorium: 'crematorium_partner_id',
  dropoff: 'dropoff_partner_id',
  transport: 'transport_partner_id',
};

// ─── Status ──────────────────────────────────────────────────────────────────

export type PartnerDocState =
  | 'satisfied'
  | 'assumed'
  | 'ready_to_send'
  | 'need_document'
  | 'awaiting_partner';

export interface PartnerDocRow {
  id: number;
  partner_id: string;
  document_type: string;
  direction: DocDirection;
  method: 'email' | 'manual' | 'upload';
  satisfied_at: string;
  created_by?: string | null;
}

export interface PartnerDocsFacts {
  funeral: {
    region?: string | null;
    cremation_status?: string | null;
    transport_dispatched_date?: string | null;
    dropoff_released_at?: string | null;
    crematorium_partner_id?: string | null;
    dropoff_partner_id?: string | null;
    transport_partner_id?: string | null;
  };
  /** The partners the case points at (any order; unknown ids are skipped). */
  partners: PartnerFacts[];
  rows: PartnerDocRow[];
  /** Latest non-archived document per type on file, with when it arrived. */
  onFile: Record<string, { receivedAt: string | null }>;
}

export interface PartnerDocItem {
  partnerId: string;
  partnerName: string;
  partnerEmail: string | null;
  partnerRegion: string | null;
  roles: PartnerRole[];
  documentType: string;
  label: { en: string; fr: string };
  defaultDirection: DocDirection;
  satisfiedByOnFile: boolean;
  state: PartnerDocState;
  /** Should staff be prompted about this item (board, objective, timeline)? */
  prompts: boolean;
  onFile: boolean;
  docReceivedAt: string | null;
  row: PartnerDocRow | null;
  /** Why it counts as assumed (state === 'assumed'). */
  assumedReason?: 'cremation_scheduled' | 'dropoff_released';
}

export interface PartnerDocsStatus {
  tracked: boolean;
  trackedSince: string;
  items: PartnerDocItem[];
}

const STATUS_ORDER = [
  'arrangement',
  'transport_dispatched',
  'cremation_scheduled',
  'ready_for_delivery',
  'delivery_scheduled',
  'delivered',
];

export function cremationScheduled(status: string | null | undefined): boolean {
  const i = STATUS_ORDER.indexOf(status || '');
  return i >= STATUS_ORDER.indexOf('cremation_scheduled');
}

/** §2d: only cases dispatched on/after the launch cutoff are tracked. A null date is untracked. */
export function isPartnerDocsTracked(
  funeral: { transport_dispatched_date?: string | null },
  since: string
): boolean {
  const d = funeral.transport_dispatched_date;
  if (!d) return false;
  return new Date(d).getTime() >= new Date(since).getTime();
}

/**
 * The partner-document requirements on a case and the state of each. Shared by
 * the objective, the process step and the UI. Items are listed for untracked
 * cases too (so staff can still send ad hoc), but never prompt.
 */
export function partnerDocsStatus(
  facts: PartnerDocsFacts,
  trackedSince: string
): PartnerDocsStatus {
  const f = facts.funeral;
  const tracked = isPartnerDocsTracked(f, trackedSince);
  const scheduled = cremationScheduled(f.cremation_status);
  const byId = new Map(facts.partners.map((p) => [String(p.id), p]));

  // (partner, document) → item; one partner filling two roles that need the
  // same document gets one item (one row satisfies both).
  const items = new Map<string, PartnerDocItem>();
  (Object.keys(ROLE_COLUMN) as PartnerRole[]).forEach((role) => {
    const partnerId = f[ROLE_COLUMN[role]];
    if (!partnerId) return;
    const partner = byId.get(String(partnerId));
    if (!partner) return;
    for (const rule of PARTNER_DOC_RULES) {
      if (!rule.enabled || rule.role !== role || !rule.match(partner)) continue;
      for (const req of rule.requirements) {
        const key = `${partner.id}|${req.documentType}`;
        const existing = items.get(key);
        if (existing) {
          if (!existing.roles.includes(role)) existing.roles.push(role);
          continue;
        }
        const row =
          facts.rows.find(
            (r) =>
              String(r.partner_id) === String(partner.id) &&
              r.document_type === req.documentType
          ) ?? null;
        const doc = facts.onFile[req.documentType];
        items.set(key, {
          partnerId: String(partner.id),
          partnerName: partner.name,
          partnerEmail: partner.email ?? null,
          partnerRegion: partner.region ?? null,
          roles: [role],
          documentType: req.documentType,
          label: req.label,
          defaultDirection: req.defaultDirection,
          satisfiedByOnFile: !!req.satisfiedByOnFile,
          state: 'satisfied',
          prompts: false,
          onFile: !!doc,
          docReceivedAt: doc?.receivedAt ?? null,
          row,
        });
      }
    }
  });

  for (const item of items.values()) {
    if (item.row) {
      item.state = 'satisfied';
    } else if (item.satisfiedByOnFile && item.onFile) {
      // On file for the case (row stays null: nothing was sent or received
      // between us and the partner).
      item.state = 'satisfied';
    } else if (scheduled) {
      item.state = 'assumed';
      item.assumedReason = 'cremation_scheduled';
    } else if (
      item.roles.every((r) => r === 'dropoff') &&
      f.dropoff_released_at
    ) {
      item.state = 'assumed';
      item.assumedReason = 'dropoff_released';
    } else if (item.defaultDirection === 'outbound') {
      item.state = item.onFile ? 'ready_to_send' : 'need_document';
    } else {
      item.state = 'awaiting_partner';
    }
    item.prompts =
      tracked &&
      item.state !== 'satisfied' &&
      item.state !== 'assumed' &&
      // The Quebec SP3 is chased by sp3_chase, and coroner / number-only cases
      // never have the document, so a missing SP3 never prompts here.
      !(item.state === 'need_document' && item.documentType === 'sp3');
  }

  return { tracked, trackedSince, items: [...items.values()] };
}

export function promptable(status: PartnerDocsStatus): PartnerDocItem[] {
  return status.items.filter((i) => i.prompts);
}

/** "the SP3 and the cremation agreement" / « le SP3 et l'entente de crémation ». */
export function docsLabel(
  items: Array<{ label: { en: string; fr: string } }>,
  lang: 'en' | 'fr'
): string {
  const labels = items.map((i) => {
    const l = i.label[lang];
    if (lang === 'fr') return /^[aeiouéèêh]/i.test(l) ? `l'${l}` : `le ${l}`;
    return `the ${l}`;
  });
  const and = lang === 'fr' ? 'et' : 'and';
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} ${and} ${
    labels[labels.length - 1]
  }`;
}

/** One board / ping line for an outstanding item. */
export function outstandingLine(
  item: Pick<PartnerDocItem, 'state' | 'label' | 'partnerName'>
): string {
  const doc = item.label.en;
  switch (item.state) {
    case 'ready_to_send':
      return `Send the ${doc} to ${item.partnerName}`;
    case 'need_document':
      return `Upload the ${doc}, then send it to ${item.partnerName}`;
    case 'awaiting_partner':
      return `Get the ${doc} from ${item.partnerName}`;
    default:
      return `${doc}: ${item.partnerName}`;
  }
}

/**
 * Board text + work-item metadata for the partner_documents objective. Items for
 * the same partner in the same state collapse into one line ("Send the SP3 and
 * the cremation agreement to Rideau"); more than one line shows the first + N.
 */
export function partnerDocsWorkItemMeta(items: PartnerDocItem[]): {
  title: string;
  outstanding: Array<{
    partnerId: string;
    partnerName: string;
    documentType: string;
    state: PartnerDocState;
    roles: PartnerRole[];
  }>;
} {
  const groups: PartnerDocItem[][] = [];
  for (const i of items) {
    const g = groups.find(
      (x) => x[0].partnerId === i.partnerId && x[0].state === i.state
    );
    if (g) g.push(i);
    else groups.push([i]);
  }
  const lines = groups.map((g) =>
    outstandingLine({
      state: g[0].state,
      partnerName: g[0].partnerName,
      label: { en: docsLabel(g, 'en').replace(/^the /, ''), fr: '' },
    })
  );
  const title = lines.length
    ? `${lines[0]}${lines.length > 1 ? ` (+${lines.length - 1})` : ''}`
    : 'Partner documents outstanding';
  return {
    title,
    outstanding: items.map((i) => ({
      partnerId: i.partnerId,
      partnerName: i.partnerName,
      documentType: i.documentType,
      state: i.state,
      roles: i.roles,
    })),
  };
}
